import { Component, HostBinding, OnDestroy, ViewChild } from '@angular/core';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { Endpoint, HelpIndex, MessageType, SearchType, SettingsType, UpdateStatusType } from '../../../../models/enums';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { CellTemplateType, GenericColDef, GridContext } from '../../../../models/grid-context';
import { ContextMenuOption } from '../../../../models/context-menu-option';
import { DeleteEntityModalComponent } from '../../delete-entity-modal/delete-entity-modal.component';
import { Workflow } from '../../../../models/workflow';
import { CreateEditEntityModalComponent } from '../../create-edit-entity-modal/create-edit-entity-modal.component';
import { Subscription } from 'rxjs/internal/Subscription';
import { XMLService } from '../../../../services/xml/xml.service';
import { isNullOrUndefined } from '../../../../models/utils';
import { Tag } from '../../../../models/tag';
import { DynamicFolder } from '../../../../models/dynamic-folder';
import * as _ from 'lodash';

@Component({
    standalone: false,
    selector: 'workflows-template',
    templateUrl: './workflows-template.component.html',
    styleUrls: ['./workflows-template.component.scss']
})
export class WorkflowsTemplateComponent implements SettingsNavigationContentComponent, OnDestroy {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;

    isDirty: boolean;
    validChanges = true;
    automationId = 'workflows-template';
    gridName = 'Workflows Templates';
    helpIndex = HelpIndex.org_workflow_template_dlg;
    selectedWorkflowName = '';
    isRefresh = false;
    expandedWorkflowHandles = [];
    private entityChangedEvent: Subscription;

    requestInfo = {
        endpoint: Endpoint.workFlowManager_getWorkFlows,
        responseName: 'workFlows',
        data: {
            projectHandle: this.commonService.currentProject?.handle,
            startIndex: 0
        }
    }

    constructor(private commonService: CommonService, private restService: RestService, private xmlService: XMLService) {
        this.entityChangedEvent = this.commonService.getMessage(MessageType.entityChanged).subscribe((data) => {
            if (data === UpdateStatusType.WORK_FLOW) {
                this.genericGrid.loadData();
                this.isRefresh = true;
                this.expandedWorkflowHandles = [...this.genericGrid.getRows()
                    .filter(r => r?.data && r.data?.handle && r.data?.isExpanded)
                    .map(r => r.data?.handle)];
            }
        });
    }

    selectedProfile?: any;

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Name',
            field: 'name',
            sortBy: 'name',
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.workflowName,
            lockVisible: true,
            colSpan: (params) => params.data.isChild ? 2 : 1,
            cellClassRules: {
                'workflow-cell': params => params.data.isChild,
            }
        },
        {
            headerName: 'Description',
            field: 'description',
            automationId: this.automationId
        },
        {
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.contextMenu,
            minWidth: 30, maxWidth: 30, width: 30,
            suppressSizeToFit: true
        },
    );
    contextMenuOptions = [
        new ContextMenuOption({
            name: 'Edit ...', iconClass: 'fa-pencil', iconColor: 'color-blue-01', automationId: this.automationId,
            click: (item) => this.editWorkflow(item),
            visible: (item: GenericTreeNode) => (item as any)?.isParent
        }),
        new ContextMenuOption({
            name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05', automationId: this.automationId,
            click: (item) => this.deleteWorkflow(item),
            visible: (item: GenericTreeNode) => (item as any)?.isParent
        })
    ];

    responseCallback = (response: any): void => {
        if (response) {
            response.workFlows = this.mapWorkflows(response.workFlows);
        }
    }

    mapWorkflows(workflows: Array<any>): Array<Workflow> {
        return workflows.map(wkf => {
            wkf.isParent = true;
            wkf.queryItems.items = wkf.queryItems.items.map((rawStep, index, arr) => {
                const xmlObj = this.xmlService.xmlToQueryConverter(rawStep.query, true);
                const searchType = xmlObj && (xmlObj as any).searchType === 'DRAdvancedSearch'
                    ? SearchType.ADVANCED
                    : SearchType.FREEFORM;

                const isFirstItem = index === 0;
                const isLastItem = index === arr.length - 1;
                const queryConverted = this.xmlService.xmlToQueryConverter(rawStep.query) || rawStep.query || '';
                const queryHighlighted = this.highlightQueryForGrid(queryConverted);
                return {
                    ...rawStep,
                    queryConverted,
                    queryHighlighted,
                    searchType,
                    isFirstItem,
                    isLastItem
                };
            });

            this.mapWorkflowSteps(wkf.queryItems.items, wkf);
            return wkf;
        });
    }

    mapWorkflowSteps(workflowSteps: Array<any>, workflow: Workflow): Array<Workflow> {
        if (!workflowSteps) {
            workflowSteps = [];
        }
        return workflowSteps.map(prj => Workflow.fromWorkflowStep(prj, workflow));
    }

    private highlightQueryForGrid(query: string): string {
        if (!query) return '';

        const hljs: any = (window as any).hljs;

        if (!hljs) {
            return query;
        }

        const str = query;

        const highlighted = hljs.highlightAuto(str);
        return (highlighted?.value || str).trim().replace(/\u00a0/g, ' ');
    }

    onCellClicked = (event): void => {
        if ((<any>event.colDef).cellTemplateType === CellTemplateType.contextMenu) {
            return;
        }
        if (event && event.data instanceof Workflow && event.data.isParent && event.node && (!event.colDef || (<any>event.colDef).cellTemplateType !== CellTemplateType.contextMenu)) {
            this.getWorkflow(event.data.handle, event, event.data);
        } else {
            this.genericGrid.expandCollapseRow(event.node);
        }
    }

    wrapTags(tags: string[]): boolean {
        const approxChipWidth = 10;
        const approxPadding = 50;

        const rows = document.getElementsByClassName('ag-workflow-row');
        if (!rows || rows.length === 0) return;

        const rowEl = rows[0] as HTMLElement;
        const availableWidth = rowEl.clientWidth;

        let totalWidth = 0;
        tags?.forEach(tag => {
            totalWidth += tag.length * approxChipWidth + approxPadding;
        });

        const shouldWrap = totalWidth > availableWidth;

        return shouldWrap;
    }

    getWorkflow = (handle?: string, event?: any, workflow?: Workflow) => {
        if (!handle && (!workflow || !workflow.handle)) {
            return;
        }
        this.restService.post(Endpoint.workFlowManager_getWorkFlow, { handle: handle }).then(response => {
            if (response && response.workFlow) {
                if (isNullOrUndefined(response.workFlow.dataSetItem)) {
                    response.workFlow.dataSetItem = { name: '' };
                }
                this.setWorkflowItems(response, workflow, event);
            }
        }).catch(error => {

        });
    }

    private setWorkflowItems(response: any, workflow: Workflow, event: any) {
        if (response.workFlow.folderItem) {
            workflow.folderItem = workflow.folderItem ? new DynamicFolder(workflow.folderItem) : null;
            workflow.folderItem.iconColor = (workflow.folderItem.attributes && workflow.folderItem.attributes.length > 0) ?
                workflow.folderItem.attributes.filter(attr => attr.name === 'folderColor')[0].value : '';
        } else {
            workflow.folderItem = null;
        }

        event.node.data = workflow;

        let node: any = this.genericGrid.getRows().filter(i => i.childIndex === event.node.childIndex);
        if (node?.length > 0) {
            node[0] = workflow;
        }

        const updated = Object.assign(new Workflow({}), event.node.data, workflow);
        event.node.setData(updated);

        this.genericGrid.expandCollapseRow(event.node);
    }

    gridContext = new GridContext({
        sortBy: 'name',
        sortAscending: true
    });

    saveChanges(autoSaveChanges?: boolean): Promise<boolean> {
        return Promise.resolve(true);
    }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        if (node?.data?.handle) {
            this.requestInfo.data.projectHandle = node.data.handle;
            this.selectedWorkflowName = node.data.name;
            this.genericGrid.loadData();
        }
    }

    deleteWorkflow(item: Workflow): void {
        if (item) {
            this.commonService.openModal(DeleteEntityModalComponent, { modalData: item, options: { title: `Delete ${item.name} in ${this.selectedWorkflowName}`, templateHandle: this.requestInfo.data.projectHandle } });
        }
    }

    editWorkflow(item: Workflow): void {
        if (item) {
            this.commonService.openModal(CreateEditEntityModalComponent, { modalData: item, options: `Edit ${item.name} in ${this.selectedWorkflowName}` });
        }
    }

    responseArrayCallback = (response: any): Workflow => {
        if (response.tagItem) {
            this.restService.post(Endpoint.workingSetManager_listWorkingSetViews, {
                workingSetHandle: this.commonService.projectData?.handle ?? response.dataSetItem.handle,
                count: 0,
                startIndex: 0,
                filters: [{
                    attribute: 'viewType',
                    value: 'TAG',
                    operator: 'EQUALS'
                }]
            }).then(responseTags => {
                if (responseTags && responseTags.views) {
                    const tagList = (<Array<any>>responseTags.views).map(t => new Tag(t));
                    response.tagItem.taggedEntityList = [];
                    response.tagItem.tags.forEach((entry) => {
                        const tag = _.filter(tagList, w => w.name === entry);
                        if (tag && tag.length > 0) {
                            response.tagItem.taggedEntityList.push(tag[0]);
                        }
                    });
                    response.tagItem = response.tagItem.taggedEntityList;
                    response.wrapTags = this.wrapTags(response.tagItem.map(t => t.name));
                }
                this.genericGrid?.gridApi?.redrawRows();
            });
        } else {
            response.tagItem = null;
        }

        return new Workflow(response);
    };

    loadDataFinishedCallback = () => {
        if (this.isRefresh) {
            const workflows = this.genericGrid.getRows();
            const expandedOrgs = workflows?.filter(item => this.expandedWorkflowHandles.includes(item?.data?.handle));
            expandedOrgs.forEach(org => { this.genericGrid.expandCollapseRow(org);});
            this.isRefresh = false;
        }
    }

    openHelp = (): void => this.commonService.openHelp(this.helpIndex);

    ngOnDestroy(): void {
        this.entityChangedEvent.unsubscribe();
    }
}